// packages/core/src/services/dateApi.js

import authService from './authService';
import { formatISO } from 'date-fns';
import config from '../config';

const API_BASE_URL = config.API_BASE_URL;
const DATES_API_PREFIX = '/api/v1/dates';
const DATE_RESOURCE_PREFIX = '/api/v1/resources/date';

/**
 * Fetches all date objects (including recurring instances) within a given range.
 * @param {Date} startDate - The start of the date range.
 * @param {Date} endDate - The end of the date range.
 * @returns {Promise<Array<Object>>} A promise that resolves to an array of date objects.
 * @throws {Error} If the request fails.
 */
export const getDatesInRange = async (startDate, endDate) => {
  if (!startDate || !endDate) {
    throw new Error("Start and end dates are required to fetch dates.");
  }
  const params = new URLSearchParams({
    start: formatISO(startDate),
    end: formatISO(endDate),
    expand_rrule: 'true',
  });
  const url = `${API_BASE_URL}${DATES_API_PREFIX}?${params.toString()}`;
  try {
    const response = await authService.authFetch(url);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to fetch dates: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('[dateApi] Error in getDatesInRange:', error);
    throw error;
  }
};

/**
 * Creates a new date object.
 * @param {Object} dateData - The data for the new date.
 * @returns {Promise<Object>} The newly created date object from the server.
 * @throws {Error} If the request fails.
 */
export const createDate = async (dateData) => {
  const url = `${API_BASE_URL}${DATE_RESOURCE_PREFIX}`;
  const payload = { ...dateData };
  if (payload.start_datetime) payload.start_datetime = formatISO(new Date(payload.start_datetime));
  if (payload.end_datetime) payload.end_datetime = formatISO(new Date(payload.end_datetime));

  console.log(`[dateApi] Creating date with payload:`, payload);
  try {
    const response = await authService.authFetch(url, {
      method: 'POST',
      body: payload,
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to create date: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('[dateApi] Error in createDate:', error);
    throw error;
  }
};

/**
 * Updates a date object, an instance of a recurring date, or a series.
 * @param {string} dateId - The ID of the date/series to update.
 * @param {Object} updatePayload - The data for the update.
 * @returns {Promise<Object>} The updated date object or series confirmation.
 * @throws {Error} If the request fails.
 */
export const updateDate = async (dateId, updatePayload) => {
  const url = `${API_BASE_URL}${DATE_RESOURCE_PREFIX}/${dateId}`;
  
  // Create a clean payload and format dates to ISO strings for the backend
  const payload = { ...updatePayload };
  if (payload.start_datetime) {
    payload.start_datetime = formatISO(new Date(payload.start_datetime));
  }
  if (payload.end_datetime) {
    payload.end_datetime = formatISO(new Date(payload.end_datetime));
  }
  if ('rrule' in payload && payload.rrule === '') {
    payload.rrule = null;
  }

  console.log(`[dateApi] Updating date ${dateId} with payload:`, payload);
  try {
    const response = await authService.authFetch(url, {
      method: 'PUT',
      body: payload,
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to update date: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error(`[dateApi] Error in updateDate for ${dateId}:`, error);
    throw error;
  }
};

/**
 * Deletes a date object or a recurring series/instance.
 * @param {string} dateId - The ID of the date to delete.
 * @param {Object} [deletePayload=null] - Optional payload for recurring deletions (e.g., scope, instance_start).
 * @returns {Promise<boolean>} True on successful deletion.
 * @throws {Error} If the request fails.
 */
export const deleteDate = async (dateId, deletePayload = null) => {
  const url = `${API_BASE_URL}${DATE_RESOURCE_PREFIX}/${dateId}`;
  console.log(`[dateApi] Deleting date ${dateId} with payload:`, deletePayload);
  try {
    const response = await authService.authFetch(url, {
      method: 'DELETE',
      ...(deletePayload && { body: deletePayload }),
    });
    if (response.status === 204 || response.ok) {
      return true;
    }
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Failed to delete date: ${response.status}`);
  } catch (error) {
    console.error(`[dateApi] Error in deleteDate for ${dateId}:`, error);
    throw error;
  }
};

const dateApi = {
  getDatesInRange,
  createDate,
  updateDate,
  deleteDate,
};

export default dateApi;